/**
 * NE4: DIE ORDNER EINES HAUSES (.592), gebaut aus den NE3-Teilen.
 *
 * ORIGINALTEXT MARVIN (02.11.2026)
 *   „Dateien hochladen wie bei NE3 mit Drag and Drop und Ordnern, damit nichts
 *    vergessen wird.", „Du sollst das Rad nicht neu erfinden. Du sollst die
 *    Sachen einbauen, die wir auch schon nutzen und haben."
 *
 * Dieselben Bausteine wie die NE3-Dateien: die Abwurffläche (`Abwurf`, Ziehen
 * und Tippen, ganze Ordner), die Kachel (`Dateikachel`) und das Dateifenster.
 * Je Ordner eine Karte mit dem Merker „Pflicht", solange ein Pflichtordner
 * leer ist. Ein ganzer Ordner vom Rechner verteilt sich auf die gleichnamigen
 * Ordner; was keinen Namen trifft, landet dort, wohin es gezogen wurde.
 */
import { useState, type ReactNode } from 'react';
import { zeitpunkt } from '../api.ts';
import Abwurf, { GAENGIG } from './Abwurf.tsx';
import { type Baumdatei } from './ordnerabwurf.ts';
import { Dateikachel } from './Dateikachel.tsx';
import { useDateifenster } from './Dateifenster.tsx';

export type Ne4Ordnerart = { name: string; pflicht: boolean; zweck?: string };

/** Welcher Ordner zu einem Pfad vom Rechner passt („Fotos/Keller/1.jpg" → Fotos). */
export function ordnerAusPfad(pfad: string, ordner: Ne4Ordnerart[], sonst: string): string {
  const teile = pfad.split('/').slice(0, -1).map((t) => t.toLowerCase());
  for (const t of teile) {
    const o = ordner.find((x) => x.name.toLowerCase() === t || x.name.toLowerCase().startsWith(t.slice(0, 4)) && t.length >= 4);
    if (o) return o.name;
  }
  return sonst;
}

export function fehlendePflicht(dateien: any[], ordner: Ne4Ordnerart[]): string[] {
  return ordner.filter((o) => o.pflicht && !dateien.some((d) => d.ordner === o.name)).map((o) => o.name);
}

/* .620: In „Kundenmails" auch Mails aus Outlook (.eml, .msg); die Prüfung am Aufbau macht der Server. */
export const KUNDENMAILS = 'Kundenmails';
const MAILARTEN = ',.eml,.msg,message/rfc822,application/vnd.ms-outlook';

export function Ne4Ordner({ dateien, ordner, darfLaden, hochladen, fertig, ordnerKnopf }: {
  dateien: any[];
  ordner: Ne4Ordnerart[];
  darfLaden: boolean;
  /** Eine Datei in einen Ordner. */
  hochladen: (ordner: string, datei: File) => Promise<any>;
  /** Nach dem Hochladen: neu laden und sagen, was ankam. */
  fertig: (text: string | null, fehler?: unknown) => void;
  /** .620: ein Knopf im Fuß eines Ordners (Kundenmails: „Auftrag an Telekom melden"). */
  ordnerKnopf?: (ordner: string) => ReactNode;
}) {
  const [laedt, setLaedt] = useState<string | null>(null);
  const { fenster, zeige } = useDateifenster();
  /* Die Auskundung kommt von der Telekom und steht vorne, wenn es sie gibt. */
  /* Ordner, die es nicht mehr gibt (Aufmaß, .595), stehen weiter da, solange Dateien darin liegen. */
  const namen = [...(dateien.some((d) => d.ordner === 'Auskundung') ? ['Auskundung'] : []), ...ordner.map((o) => o.name),
    ...[...new Set<string>(dateien.map((d) => d.ordner))].filter((n) => n !== 'Auskundung' && !ordner.some((o) => o.name === n))];
  const kachel = (d: any) => ({ id: d.id, dateiname: d.dateiname, mime: d.mime, groesse: d.groesse, adresse: `/api/ne4/dateien/${d.id}` });

  async function laden(stuecke: Baumdatei[], ziel: string, marke = ziel) {
    if (!stuecke.length) return;
    setLaedt(marke);
    const je: Record<string, number> = {};
    try {
      for (const s of stuecke) {
        const o = ordnerAusPfad(s.pfad, ordner, ziel);
        await hochladen(o, s.datei);
        je[o] = (je[o] ?? 0) + 1;
      }
      fertig(Object.entries(je).map(([o, n]) => `${n === 1 ? '1 Datei' : `${n} Dateien`} in „${o}"`).join(', ') + ' hochgeladen.');
    } catch (x) { fertig(null, x); } finally { setLaedt(null); }
  }

  return (
    <div>
      {darfLaden && (
        <Abwurf titel="Dateien oder ganze Ordner hierher" ordner klein accept={GAENGIG}
                laeuft={laedt === '*'} laufText="Lädt hoch"
                hinweis="Unterordner mit gleichem Namen (Fotos, Dokumentation) landen dort, alles andere unter Sonstiges. Oder direkt auf einen Ordner unten ziehen."
                nimm={(x) => void laden(x, 'Sonstiges', '*')} />
      )}
      {namen.map((n) => {
        const meta = ordner.find((o) => o.name === n);
        const drin = dateien.filter((d) => d.ordner === n);
        const hat = drin.length > 0;
        const fuellbar = darfLaden && n !== 'Auskundung' && (!!meta || n === 'Sonstiges');
        return (
          <div key={n} className={'ordnerkarte ' + (hat ? 'hat' : meta?.pflicht && fuellbar ? 'leer' : 'frei')} style={{ marginTop: 10 }}>
            <div className="reihe kopfzeile">
              <span className="punkt" />
              <b className="wachsen">{n}</b>
              <span className="klein leise">{hat ? `${drin.length} ${drin.length === 1 ? 'Datei' : 'Dateien'}` : 'leer'}</span>
              {meta?.pflicht && !hat && <span className="merker warnung">Pflicht</span>}
              {n === 'Auskundung' && <span className="merker">von der Telekom</span>}
            </div>
            {meta?.zweck && !hat && <div className="klein leise zweck">{meta.zweck}</div>}
            {hat && (
              <div className="dateigitter">
                {drin.map((d) => (
                  <Dateikachel key={d.id} datei={kachel(d)} oeffne={(x) => zeige(x, drin.map(kachel))}>
                    {/* .620: eine Kundenmail zeigt Betreff, Absender und Datum der Mail (deutsche Zeit). */}
                    {d.mail_betreff || d.mail_von
                      ? <span className="klein" style={{ display: 'block' }}>
                          {d.mail_betreff && <b style={{ display: 'block' }}>{d.mail_betreff}</b>}
                          {d.mail_von && <span style={{ display: 'block' }}>{d.mail_von}</span>}
                          {d.mail_datum && <span className="leise" style={{ display: 'block' }}>{zeitpunkt(d.mail_datum)}</span>}
                        </span>
                      : <span className="klein leise" style={{ display: 'block' }}>{zeitpunkt(d.created_at)}{d.von ? `, ${d.von}` : ''}</span>}
                  </Dateikachel>
                ))}
              </div>
            )}
            {fuellbar && (
              <div className="ordnerfuss" style={ordnerKnopf?.(n) ? { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' } : undefined}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <Abwurf titel={`In „${n}"`} klein accept={n === KUNDENMAILS ? GAENGIG + MAILARTEN : GAENGIG} laeuft={laedt === n} laufText="Lädt hoch"
                          hinweis={n === KUNDENMAILS ? 'Mail aus Outlook hierher ziehen (.eml, .msg) oder tippen' : undefined}
                          nimm={(x) => void laden(x.map((s) => ({ ...s, pfad: s.datei.name })), n)} />
                </div>
                {ordnerKnopf?.(n)}
              </div>
            )}
          </div>
        );
      })}
      {fenster}
    </div>
  );
}
